import { formatObservationValue, type WithId } from '@medplum/core';
import type { Observation } from '@medplum/fhirtypes';
import { ChevronRightIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@liza/ui/utils';
import { formatDate } from '../../utils/date';
import { useCodeDisplay } from '../../hooks/useCodeDisplay';
import { extractReferenceRange, getObservationIdentifier } from '../../utils/observation';
import { Item, ItemActions, ItemContent, ItemDescription, ItemTitle } from '../ui/item';
import { type PromotionStatus, RecordPromotionButton } from './RecordPromotionButton';

export type LabsSummaryProps = {
  labs: WithId<Observation>[];
  onViewAll?: () => void;
  onSelectLab?: (labIdentifier: string) => void; // Code or name identifier
  selectedLabIdentifier?: string;
  onPromotionClick?: (labIdentifier: string) => void;
  getPromotionStatus?: (labIdentifier: string) => PromotionStatus;
};

type GroupedLab = {
  identifier: string; // Code or name used for grouping
  name: string; // Display name
  results: WithId<Observation>[];
  latest: WithId<Observation>;
};

const LabCard = ({
  groupedLab,
  onClick,
  onPromotionClick,
  promotionStatus,
  selected,
}: {
  groupedLab: GroupedLab;
  onClick?: () => void;
  onPromotionClick?: () => void;
  promotionStatus?: PromotionStatus;
  selected?: boolean;
}): React.ReactElement => {
  const { t } = useTranslation('records');
  const { name, results, latest } = groupedLab;
  const resultCount = results.length;
  const latestDate = formatDate(latest.effectiveDateTime);
  const value = formatObservationValue(latest) || t('unknown');
  const latestReferenceRange = extractReferenceRange(latest);
  const referenceRange = latestReferenceRange ? `Ref: ${latestReferenceRange}` : '';

  const content = (
    <>
      <ItemContent>
        <div className="flex items-baseline justify-between gap-2">
          <ItemTitle className="text-sm font-medium">{name}</ItemTitle>
          <span className="text-sm">{value}</span>
        </div>
        <ItemDescription className="text-xs">
          {t('latestResultCount', { date: latestDate, count: resultCount })}
          {referenceRange && <span className="block">{referenceRange}</span>}
        </ItemDescription>
      </ItemContent>
      <ItemActions>
        {onPromotionClick && <RecordPromotionButton onClick={onPromotionClick} status={promotionStatus} />}
        {onClick && <ChevronRightIcon className="size-4 text-muted-foreground" />}
      </ItemActions>
    </>
  );

  if (onClick) {
    return (
      <Item
        size="sm"
        className={cn(
          'cursor-pointer hover:bg-accent/50',
          selected && 'bg-accent text-accent-foreground hover:bg-accent',
        )}
        onClick={onClick}
        aria-current={selected ? 'true' : undefined}
      >
        {content}
      </Item>
    );
  }

  return <Item size="sm">{content}</Item>;
};

// oxlint-disable-next-line react/no-multi-comp
export const LabsSummary = ({
  labs,
  onViewAll,
  onSelectLab,
  selectedLabIdentifier,
  onPromotionClick,
  getPromotionStatus,
}: LabsSummaryProps): React.ReactElement | null => {
  const { t } = useTranslation('records');
  const { displayFor } = useCodeDisplay();

  // Group labs by code (LOINC preferred) or fallback to name
  const groupedLabsMap = new Map<string, WithId<Observation>[]>();
  for (const lab of labs) {
    const groupKey = getObservationIdentifier(lab);
    const existing = groupedLabsMap.get(groupKey) || [];
    groupedLabsMap.set(groupKey, [...existing, lab]);
  }

  // Convert to array and sort by latest date
  const groupedLabs: GroupedLab[] = Array.from(groupedLabsMap.entries())
    .map(([identifier, results]) => {
      const sorted = [...results].sort(
        (a, b) => new Date(b.effectiveDateTime || '').getTime() - new Date(a.effectiveDateTime || '').getTime(),
      );
      const latest = sorted[0];
      if (!latest) return null;
      const displayName = displayFor(latest.code);
      return {
        identifier,
        name: displayName,
        results: sorted,
        latest,
      };
    })
    .filter((lab): lab is GroupedLab => lab !== null)
    .sort(
      (a, b) =>
        new Date(b.latest.effectiveDateTime || '').getTime() - new Date(a.latest.effectiveDateTime || '').getTime(),
    );

  if (onViewAll && groupedLabs.length === 0) {
    return null;
  }

  // If onViewAll is provided, show a single summary card
  if (onViewAll && !onSelectLab) {
    const mostRecent = groupedLabs[0];
    return (
      <Item className="cursor-pointer" onClick={onViewAll}>
        <ItemContent>
          <ItemTitle className="text-lg font-medium">{t('labs')}</ItemTitle>
          <ItemDescription className="text-xs text-muted-foreground">
            {mostRecent && (
              <>
                <span className="text-muted-foreground">{t('mostRecent')} </span>
                <span className="font-medium">{mostRecent.name}</span>
                <span className="text-muted-foreground">
                  {' '}
                  {t('on')} {formatDate(mostRecent.latest.effectiveDateTime)}
                </span>
              </>
            )}
          </ItemDescription>
        </ItemContent>
        <ItemActions>
          <ChevronRightIcon className="size-4 text-muted-foreground" />
        </ItemActions>
      </Item>
    );
  }

  // Show individual lab cards
  return (
    <div>
      {groupedLabs.map((groupedLab) => (
        <LabCard
          key={groupedLab.identifier}
          groupedLab={groupedLab}
          onClick={onSelectLab ? (): void => onSelectLab(groupedLab.identifier) : undefined}
          selected={selectedLabIdentifier === groupedLab.identifier}
          onPromotionClick={onPromotionClick ? (): void => onPromotionClick(groupedLab.identifier) : undefined}
          promotionStatus={getPromotionStatus?.(groupedLab.identifier)}
        />
      ))}
    </div>
  );
};
